import React from 'react';
import { Station, StationType } from '../Firebase/firebaseApi';
import { Card, CardActionArea, CardMedia, CardContent, Typography, Box, CircularProgress } from '@mui/material';

interface StationListProps {
  allStations: Station[];
  availableStations: Station[];
  unavailableStationIds: Set<string>;
  hasSelectedDates: boolean;
  stationTypes: StationType[];
  availabilityLoading: boolean;
  onStationClick: (station: Station) => void;
  /** Recurring: conflict dates per station id */
  stationConflictDates?: Record<string, string[]>;
  seriesTotalCount?: number;
}

const StationList: React.FC<StationListProps> = ({
  allStations,
  availableStations,
  unavailableStationIds,
  hasSelectedDates,
  stationTypes,
  availabilityLoading,
  onStationClick,
  stationConflictDates = {},
  seriesTotalCount = 0,
}) => {
  if (availabilityLoading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '200px', direction: 'rtl' }}>
        <CircularProgress />
        <Typography variant="h6" sx={{ ml: 2 }}>
          בודק זמינות תחנות...
        </Typography>
      </Box>
    );
  }

  if (allStations.length === 0) {
    return (
      <Box sx={{ textAlign: 'center', py: 4, direction: 'rtl' }}>
        <Typography variant="h6" color="text.secondary">
          בחר סוג עמדה להצגת תחנות
        </Typography>
      </Box>
    );
  }

  const isSeriesMode = seriesTotalCount > 0;

  return (
    <Box sx={{ mt: 4, direction: 'rtl' }}>
      <Box 
        sx={{ 
          display: 'grid', 
          gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))', 
          gap: 3, 
          justifyItems: 'center' 
        }}
      >
        {allStations.map((station) => {
          const id = String(station.id);
          const isUnavailable = unavailableStationIds.has(id);
          const conflicts = stationConflictDates[id] || [];
          const isPartial =
            isSeriesMode &&
            hasSelectedDates &&
            !isUnavailable &&
            conflicts.length > 0;
          const freeCount = seriesTotalCount - conflicts.length;
          const isAvailable = hasSelectedDates && !isUnavailable;
          const showNeedsDate = !hasSelectedDates;

          let badgeLabel = 'בחר תאריך';
          let badgeColor: 'success' | 'error' | 'warning' | 'default' = 'default';
          if (hasSelectedDates) {
            if (isUnavailable) {
              badgeLabel = 'תפוס';
              badgeColor = 'error';
            } else if (isPartial) {
              badgeLabel = `חלקי ${freeCount}/${seriesTotalCount}`;
              badgeColor = 'warning';
            } else {
              badgeLabel = 'זמין';
              badgeColor = 'success';
            }
          }

          const badgeBg =
            badgeColor === 'success'
              ? 'success.light'
              : badgeColor === 'error'
                ? 'error.light'
                : badgeColor === 'warning'
                  ? 'warning.light'
                  : 'grey.200';
          const badgeText =
            badgeColor === 'success'
              ? 'success.main'
              : badgeColor === 'error'
                ? 'error.main'
                : badgeColor === 'warning'
                  ? 'warning.dark'
                  : 'text.secondary';
          
          // Find the station type for this station to get the image
          const stationType = stationTypes.find(type => type.id === station.typeId);
          return (
            <Card 
              key={station.id}
              sx={{ 
                width: '100%',
                maxWidth: 350,
                textAlign: 'right', 
                direction: 'rtl',
                transition: 'transform 0.2s, box-shadow 0.2s',
                display: 'flex',
                flexDirection: 'column',
                position: 'relative',
                opacity: showNeedsDate ? 0.8 : (isAvailable ? 1 : 0.6),
                backgroundColor: showNeedsDate ? 'background.paper' : (isAvailable ? 'background.paper' : 'grey.100'),
                border: showNeedsDate ? '1px dashed' : (isPartial ? '2px solid' : (isAvailable ? 'none' : '2px solid')),
                borderColor: showNeedsDate
                  ? 'grey.400'
                  : isPartial
                    ? 'warning.light'
                    : isAvailable
                      ? 'transparent'
                      : 'error.light',
                '&:hover': isAvailable ? {
                  transform: 'translateY(-4px)',
                  boxShadow: 4
                } : {}
              }}
            >
              <Typography 
                variant="body2" 
                sx={{ 
                  position: 'absolute',
                  top: 8,
                  right: 8,
                  zIndex: 2,
                  color: badgeText,
                  fontWeight: 'bold',
                  backgroundColor: badgeBg,
                  px: 1,
                  py: 0.5,
                  borderRadius: 1,
                  fontSize: '0.75rem',
                  boxShadow: 1
                }}
              >
                {badgeLabel}
              </Typography>
              
              <CardActionArea 
                onClick={() => isAvailable ? onStationClick(station) : null} 
                disabled={!isAvailable}
                sx={{ 
                  height: '100%', 
                  display: 'flex', 
                  flexDirection: 'column',
                  cursor: isAvailable ? 'pointer' : (showNeedsDate ? 'default' : 'not-allowed')
                }}
              >
                {stationType?.imageLink && (
                  <CardMedia 
                    component="img" 
                    height="180" 
                    image={stationType.imageLink} 
                    alt={`עמדה ${station.id} - ${stationType.typeFriendlyName}`}
                    sx={{ objectFit: 'cover' }}
                  />
                )}
                <CardContent sx={{ flexGrow: 1, display: 'flex', flexDirection: 'column' }}>
                  <Box sx={{ mb: 2 }}>
                    <Typography variant="h6" sx={{ color: 'primary.main', fontWeight: 'bold' }}>
                       עמדה: {station.id}
                    </Typography>
                    {isPartial && (
                      <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5 }}>
                        {conflicts.length} מועדים תפוסים ידולגו בהזמנה
                      </Typography>
                    )}
                  </Box>
                </CardContent>
              </CardActionArea>
            </Card>
          );
        })}
      </Box>
    </Box>
  );
};

export default StationList;
